import { Button, Group, Loader, Modal, Stack, Text, Textarea } from "@mantine/core"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { DeliveryApi } from "../../apis"
import { IDeliveryNotePlus } from "../../models/DeliveryNote"
import { message } from "../../utils/message"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"

export type BulkPodMode = "verify" | "reject"

type Props = Readonly<{
  mode: BulkPodMode
  rows: IDeliveryNotePlus[]
  onClose: () => void
  /** Called after the run: clears the selection and reloads the list. */
  onDone: () => void
}>

const CHUNK = 8

/**
 * Бөөнөөр баталгаажуулах / татгалзах: the DN detail's proof decision (BE delivery-note/pod-verify.ts,
 * pod-reject.ts) applied to each selected delivered note. There is no bulk endpoint and the list does
 * not carry the proof status, so on open each proof is read once; notes without a proof, or already
 * decided, are skipped (podVerify / podReject do not apply to them). The reject reason is asked once.
 */
export function BulkPodDialog({ mode, rows, onClose, onDone }: Props) {
  const { t } = useTranslation(["delivery", "common"])
  const { stateOf } = useDeliveryActions()
  const action = mode === "verify" ? "podVerify" : "podReject"
  const [eligible, setEligible] = useState<IDeliveryNotePlus[] | null>(null)
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const ok: IDeliveryNotePlus[] = []
      for (let i = 0; i < rows.length; i += CHUNK) {
        const part = rows.slice(i, i + CHUNK)
        const proofs = await Promise.all(
          // getProof already maps 404 to null; anything thrown here is a real error (5xx, timeout, 403).
          part.map((r) => DeliveryApi.getProof(r.id!).catch(() => "unreadable" as const)),
        )
        part.forEach((r, idx) => {
          const proof = proofs[idx]
          // An unreadable proof is not "no proof": keep the note so the POST reports the real error by code.
          if (proof === "unreadable" || (proof && stateOf(action, { status: proof.verificationStatus }).applies)) ok.push(r)
        })
      }
      if (!cancelled) setEligible(ok)
    }
    run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trimmed = reason.trim()
  const reasonShort = mode === "reject" && trimmed.length < 3
  const skipped = eligible ? rows.length - eligible.length : 0

  const close = () => {
    if (!submitting) onClose()
  }

  const onSubmit = async () => {
    if (!eligible || eligible.length === 0 || reasonShort) return
    setSubmitting(true)
    const failed: string[] = []
    let lastError: unknown = null
    try {
      for (const r of eligible) {
        try {
          if (mode === "verify") await DeliveryApi.verifyProof(r.id!)
          else await DeliveryApi.rejectProof(r.id!, trimmed)
        } catch (err) {
          lastError = err
          failed.push(r.refCode || r.id || "-")
        }
      }
      const done = eligible.length - failed.length
      if (failed.length === 0) {
        message.success(t(mode === "verify" ? "delivery.pod.bulkVerifySuccess" : "delivery.pod.bulkRejectSuccess", { count: done }))
      } else if (eligible.length === 1) {
        message.error(translateBackendError(lastError))
      } else {
        message.error(t("delivery.pod.bulkPartial", { done, failed: failed.length, codes: failed.join(", ") }))
      }
      onDone()
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      opened
      onClose={close}
      centered
      title={t(mode === "verify" ? "delivery.pod.bulkVerifyTitle" : "delivery.pod.bulkRejectTitle")}
    >
      {eligible === null ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : (
        <Stack gap="md">
          <Text fz="var(--nw-fz-body)">{t("delivery.pod.bulkBody", { count: eligible.length })}</Text>
          {skipped > 0 && (
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
              {t("delivery.pod.bulkSkipped", { count: skipped })}
            </Text>
          )}
          {mode === "reject" && eligible.length > 0 && (
            <Textarea
              label={t("delivery.pod.rejectReason")}
              placeholder={t("delivery.pod.rejectPlaceholder")}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              minRows={3}
              autosize
              required
              maxLength={1000}
              error={reason.length > 0 && reasonShort ? t("delivery.pod.rejectReasonShort") : undefined}
            />
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={close} disabled={submitting}>
              {t("common:actions.dismiss")}
            </Button>
            <Button
              onClick={onSubmit}
              loading={submitting}
              disabled={eligible.length === 0 || reasonShort}
              color={mode === "verify" ? undefined : "gray"}
            >
              {t(mode === "verify" ? "delivery.pod.verify" : "delivery.pod.reject")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )
}
